// START IMPORTS
// Context imports
import {
  useAppConstantsContext,
  AppConfig,
  useSubscriptionAccessConfigContext,
} from '@ltvco/refresh-lib/ctx';
// Family tree imports
import {
  useFamilyTreeReports,
  AddToFamilyTreeButton,
  formatFamilyTreeDataFromObituaryReport,
  useFocusedFamilyTreeMember,
} from '@ltvco/refresh-lib/fmt';
// Hooks imports
import {
  useLLMObituaryParser,
  useLLMObituaryReport,
  useReport,
} from '@ltvco/refresh-lib/hooks';
// payments imports
import {
  ReactivationDaysLeft,
  uncancelBillingVariation2Value,
  uncancelBillingPbrFeatureName,
  uncancelBillingControlValue,
} from '@ltvco/refresh-lib/payments';
// Theme and UI imports
import { Card, Grid, Box, styled } from '@ltvco/refresh-lib/theme';
// Utility imports
import {
  isZeroed,
  useScrollToSectionOnNavigate,
} from '@ltvco/refresh-lib/utils';
// Core lib v1 imports
import {
  DebugMenu,
  DesktopNav,
  ObituaryReport,
  ObituaryReportLoading,
  ReportLoading,
  ReportNavMobile,
} from '@ltvco/refresh-lib/v1';
// Core lib v2 imports
import {
  FullObituary,
  NullableAdvancedSearchURLParams,
  ReportGeneratingScreen,
  ReportNullState,
  ObituaryGeneralInformation,
  ObituaryHeader,
  ObituarySmartInsight,
  ObituaryPersonalInterest,
  ObituaryFuneralDetails,
  ObituaryRelatives,
  ObituaryLifeEvents,
  PersonSearchCta,
  LifeStorySection,
} from '@ltvco/refresh-lib/v2';
import { useFeatureValue, useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// App-specific imports
import { constants } from 'appConstants';
import { getObituaryNavLinkData } from 'navLinkData/obituaryNavLinkData';

// Component imports
import { AncestorSearchBanner } from './SearchBanners/AncestorSearchBanner';

// Type imports
import type { ReportRouteProps } from './types';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface ObituaryReportProps extends ReportRouteProps {}
// END INTERFACES_AND_TYPES

// START HELPER_FUNCTIONS
function parseDate(date: string) {
  if (!date) {
    return false;
  }
  const [month, day, year] = date.split('/').map(Number);
  return {
    full: date,
    parsed: {
      year,
      month,
      day,
    },
  };
}
// END HELPER_FUNCTIONS

// START STYLED_COMPONENTS
const StyledContainer = styled(Box)(({ theme }) => ({
  margin: 'auto',
  padding: theme.spacing(2),
  borderRadius: '20px',
  backgroundColor: '#fff',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    padding: '20px',
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: 'none',
  backgroundRepeat: 'repeat',
  backgroundSize: 'contain',
  backgroundPosition: 'center',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: '0',
  [theme.breakpoints.up('sm')]: {
    paddingTop: '80px',
    paddingLeft: '40px',
    paddingRight: '40px',
    paddingBottom: '20px',
  },
}));
// END STYLED_COMPONENTS

export function ObituaryReportPage({
  permalink,
  firstReportScreen,
}: ObituaryReportProps) {
  // START HOOKS_AND_QUERIES
  const {
    data: { brandName },
  } = useAppConstantsContext();
  const { isHidden } = useSubscriptionAccessConfigContext();
  const queryResult = useReport(permalink);
  const { hash } = useLocation();
  const { trackEvent, trackEventGA4 } = useContext(AppConfig);
  const navigate = useNavigate();
  const { navigateToFocusedMember } = useFocusedFamilyTreeMember();
  const { data: reportsData, isLoading: isLoadingTreeReports } =
    useFamilyTreeReports({
      bodyOptions: {
        report_type: ['ObituaryReport'],
        placeholders_excluded: true,
      },
      logNotFoundErrorEnabled: false,
    });
  const [, triggerUpdate] = useState(0);
  const uncancelFeatureValue = useFeatureValue(
    uncancelBillingPbrFeatureName,
    uncancelBillingControlValue
  );
  const uncancelFeatureIsOn = useFeatureIsOn(uncancelBillingPbrFeatureName);
  // END HOOKS_AND_QUERIES

  // START DERIVED_DATA_AND_UTILS
  const zeroed = isZeroed(queryResult);
  const report = new ObituaryReport(queryResult?.data as any);
  const obituary = report.data.obituaries[0];

  const { data: llmResponse, isLoading } = useLLMObituaryParser({
    enabled: Boolean(obituary),
    obituary,
    customSufixStorageKey: 'obituaryParser',
    shouldSuspendExecution:
      !constants.featureFlags.obituaryParserEnabled || Boolean(!obituary),
  });

  const llmAccomplishments = llmResponse?.data?.accomplishments || [];
  const llmEducation = llmResponse?.data?.educationLevel || [];
  const llmCareer = llmResponse?.data?.jobs || [];
  const llmAge = llmResponse?.data?.age || '';
  const llmParsedNicknames = llmResponse?.data?.nicknames?.join('') || '';
  const llmRelatives = llmResponse?.data?.relatives || [];
  const llmService = llmResponse?.data?.service || null;
  const llmVisitation = llmResponse?.data?.visitation || null;
  const llmDonations = llmResponse?.data?.donations || [];
  const activities = llmResponse?.data?.activities || [];
  const hobbies = llmResponse?.data?.hobbies || [];
  const miscellaneous = llmResponse?.data?.miscellaneous || [];
  const llmParsedDOB = parseDate(llmResponse?.data?.dateOfBirth) || null;
  const llmParsedDOD = parseDate(llmResponse?.data?.dateOfDeath) || null;
  const llmLocation = llmResponse?.data?.location || null;
  const llmPlaceOfBirth = llmResponse?.data?.placeOfBirth || null;

  const params = {
    obituary_text: obituary?.message || '',
    date_of_birth: llmParsedDOB?.full,
    date_of_death: llmParsedDOD?.full,
    place_of_birth: llmPlaceOfBirth,
    residence:
      obituary?.recentCity && obituary?.recentRegion
        ? `${obituary.recentCity}, ${obituary.recentRegion}`
        : llmLocation || '',
    family_members: JSON.stringify(llmRelatives),
    achievements: JSON.stringify(llmAccomplishments),
  };

  const { summaryData, isLoading: isLoadingLifeStory } = useLLMObituaryReport({
    params,
  });

  useScrollToSectionOnNavigate(hash, queryResult);
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  trackEvent('view full report', 'view', 'obituary search');
  // END EFFECTS

  // START EARLY_RETURNS
  if (firstReportScreen?.isActive) {
    return (
      <ReportGeneratingScreen
        reportType="obituary"
        ready={!queryResult.isLoading}
        onComplete={() => {
          firstReportScreen?.onComplete();
          triggerUpdate((n) => n + 1);
        }}
      />
    );
  }

  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={2} />;
  }
  if (zeroed) {
    return <ReportNullState />;
  }

  if (!report) {
    return <ReportLoading menuItems={2} />;
  }

  if (!report?.data?.obituaries && !report?.data?.obituaries?.length) {
    return <ReportNullState />;
  }
  // END EARLY_RETURNS

  // START DATA_PREPARATION
  const {
    id,
    name,
    dob,
    dod,
    funeralHome,
    funeralHomeAddress,
    funeralHomeUrl,
    message,
    photoUrl,
    nickname,
    personUrl,
    recentCity,
    recentRegion,
  } = obituary;

  document.title = `${name} - ${brandName}`;

  const reportInFamilyTree: boolean | undefined = reportsData?.reports
    ? !!reportsData?.reports?.some((report) => {
        return 'obituary_id' in report && report.obituary_id === id;
      })
    : undefined;

  const locationValue =
    recentCity && recentRegion
      ? `${recentCity}, ${recentRegion}`
      : llmLocation || '';

  const personIdentity = obituary.rawData.person.identity;

  const obituarySearchParams: NullableAdvancedSearchURLParams = {
    fullName: name,
    fName: personIdentity?.names?.[0]?.parsed?.first || '',
    lName: personIdentity?.names?.[0]?.parsed?.last || '',
    birthMonth:
      dob?.parsed?.month?.toString().padStart(2, '0') ||
      llmParsedDOB?.parsed.month.toString().padStart(2, '0') ||
      null,
    birthYear:
      dob?.parsed?.year?.toString() ||
      llmParsedDOB?.parsed.year.toString() ||
      null,
    deathMonth:
      dod?.parsed?.month?.toString().padStart(2, '0') ||
      llmParsedDOD?.parsed.month.toString().padStart(2, '0') ||
      null,
    deathYear:
      dod?.parsed?.year?.toString() ||
      llmParsedDOD?.parsed.year.toString() ||
      null,
    city: recentCity,
    state: recentRegion,
    location: locationValue,
    country: funeralHomeAddress?.parsed?.country || '',
  };

  const obituaryNavLinkData = getObituaryNavLinkData(llmResponse?.data);

  const hideFamilyTreeCTA = isHidden('ancestry');
  const hidePersonCTA = isHidden('person');
  // END DATA_PREPARATION

  // START EVENT_HANDLERS
  const handleAddingRelativeToFamilyTree = async () => {
    if (!obituary) {
      return null;
    }

    const reportIndi = formatFamilyTreeDataFromObituaryReport(
      id,
      name,
      dob || llmParsedDOB,
      dod || llmParsedDOD,
      recentCity,
      recentRegion,
      permalink,
      photoUrl
    );

    trackEventGA4({
      event_name: 'report_element_engagement',
      type: 'added_to_tree',
      interacted_from: 'obituary_report',
      report_type: 'obituary',
    });

    if (reportInFamilyTree) {
      navigateToFocusedMember(memberIdInFamilyTree as number);
      return;
    }

    localStorage.setItem('familyTreeRelativeToAdd', JSON.stringify(reportIndi));

    // Obituary report is not posting to ancestry tree
    // Instead redirect to the family tree page
    // so the user can search for a person report
    // and create a family tree for the person
    // This is the only report that is not posting to ancestry tree
    navigate('/family-tree');
  };

  const memberIdInFamilyTree = reportInFamilyTree
    ? reportsData?.reports?.find((report) => {
        return 'obituary_id' in report && report.obituary_id === id;
      })?.member_id
    : undefined;
  // END EVENT_HANDLERS

  // START RENDER
  return (
    <>
      <Grid container direction={'row'} columns={12} spacing={7}>
        <Grid
          size={{ sm: 12, md: 4, lg: 4 }}
          sx={{ display: { xs: 'none', sm: 'none', md: 'block', lg: 'block' } }}
        >
          <DesktopNav
            navLinkData={obituaryNavLinkData}
            reportDataPoint={name}
            permalink={permalink}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 8, lg: 8 }} marginTop={3}>
          {uncancelFeatureIsOn &&
            uncancelFeatureValue === uncancelBillingVariation2Value && (
              <ReactivationDaysLeft
                direction="row"
                isInReport
                customPadding={'0px 0 16px 0'}
              />
            )}
          <StyledCard
            id="obituary-overview"
            style={{
              backgroundImage: `url(${constants.images.imgTreeOfLife})`,
            }}
          >
            <StyledContainer>
              <Box mb={hideFamilyTreeCTA ? 3.5 : 0}>
                <ObituaryHeader
                  personName={name}
                  nickname={nickname || llmParsedNicknames}
                  personImage={photoUrl}
                  age={llmAge}
                  dob={dob || llmParsedDOB}
                  dod={dod || llmParsedDOD}
                  funeralHome={funeralHome}
                  funeralHomeAddress={funeralHomeAddress}
                  funeralHomeUrl={funeralHomeUrl}
                  photoUrl={photoUrl}
                  personUrl={personUrl}
                />
              </Box>
              {!hideFamilyTreeCTA && (
                <AddToFamilyTreeButton
                  reportInFamilyTree={reportInFamilyTree}
                  addRelativeToFamilyTree={handleAddingRelativeToFamilyTree}
                  isLoading={isLoadingTreeReports}
                />
              )}
              <ObituaryGeneralInformation
                dob={dob || llmParsedDOB}
                dod={dod || llmParsedDOD}
                placeOfBirth={llmPlaceOfBirth}
                location={locationValue}
                imgInfinity={constants.images.imgInfinity}
                isLoading={isLoading}
              />
            </StyledContainer>
          </StyledCard>
          <FullObituary text={message} personName={name} />
          {isLoading && <ObituaryReportLoading />}
          <ObituarySmartInsight isLoading={isLoading} />
          <LifeStorySection
            text={summaryData.overview}
            personName={name}
            isLoading={isLoadingLifeStory}
          />
          <ObituaryRelatives relatives={llmRelatives} name={name} />
          <Box mb={2}>
            {!hideFamilyTreeCTA && (
              <AncestorSearchBanner
                searchParams={{
                  ...obituarySearchParams,
                  // The state is being sent empty to get more relevant results if the country is not United States
                  state:
                    obituarySearchParams.country?.toLowerCase() ===
                    'united states'
                      ? obituarySearchParams.state
                      : '',
                  // This is being sent empty to get more relevant results
                  birthYear: '',
                  deathYear: '',
                }}
              />
            )}
          </Box>
          <ObituaryLifeEvents
            llmAccomplishments={llmAccomplishments}
            llmEducation={llmEducation}
            llmCareer={llmCareer}
            name={name}
          />
          <ObituaryPersonalInterest
            activities={activities}
            hobbies={hobbies}
            miscellaneous={miscellaneous}
            personName={name}
          />
          <ObituaryFuneralDetails
            service={llmService}
            visitation={llmVisitation}
            donations={llmDonations}
            funeralHomeUrl={funeralHomeUrl}
          />
          {!hidePersonCTA && (
            <PersonSearchCta
              image={constants.images.imgPersonSearch}
              personName={name}
              location={locationValue}
            />
          )}
        </Grid>
      </Grid>

      {/* START MOBILE_NAVIGATION */}
      <ReportNavMobile navLinkData={obituaryNavLinkData} />
      {/* END MOBILE_NAVIGATION */}

      {/* START DEBUG_MENU */}
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
      {/* END DEBUG_MENU */}
    </>
  );
  // END RENDER
}
